import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, filter, fromEvent, from, timer } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { environment } from '../../../environments/environment';
import { APP_VERSION } from '../constants/app-version';
import {
  AppVersionInfo,
  VersionCheckConfig,
} from '../interfaces/app-version.interface';
import { UnsavedChangesService } from './unsaved-changes.service';

/** BroadcastChannel name used to sync version state across tabs of this app. */
const VERSION_CHANNEL = 'sukna-app-version';

/** Query param appended by the cache-busting reload; stripped once the app boots. */
const RELOAD_PARAM = '_v';

/**
 * Soft-update detection.
 *
 * Running fingerprint = APP_VERSION baked into the JS bundle at build time.
 * Remote fingerprint = `version.json` on the server (fetched with cache: 'no-store').
 * When they differ, prompt the user to reload.
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private readonly translate = inject(TranslateService);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly unsavedChanges = inject(UnsavedChangesService);

  private readonly config: VersionCheckConfig = environment.versionCheck;

  /** Version compiled into this tab's JS bundle. */
  private readonly runningVersion: AppVersionInfo = { ...APP_VERSION };
  private started = false;
  private promptOpen = false;
  private notifiedVersion: string | null = null;
  private reloading = false;
  private subscriptions = new Subscription();
  private destroyHookRegistered = false;

  private channel: BroadcastChannel | null = null;
  private beforeUnloadHandler: ((event: BeforeUnloadEvent) => void) | null = null;

  start(): void {
    // Remove the cache-busting marker left by a soft-update reload regardless of
    // whether version checking is enabled, so it never lingers in the address bar.
    this.cleanupReloadParam();

    if (this.started || !this.config.enabled) {
      return;
    }
    this.started = true;

    if (!this.destroyHookRegistered) {
      this.destroyHookRegistered = true;
      this.destroyRef.onDestroy(() => this.stop());
    }

    this.log('Started', {
      running: this.runningVersion,
      pollIntervalMs: this.config.pollIntervalMs,
    });

    this.registerCrossTabSync();
    this.registerUnloadGuard();

    // Immediate check on open (covers close-tab / reopen with a stale cached shell).
    this.checkForUpdate();

    const intervalMs = Math.max(this.config.pollIntervalMs, 10_000);
    this.subscriptions.add(
      timer(intervalMs, intervalMs)
        .pipe(
          filter(() => !this.promptOpen),
          switchMap(() => from(this.fetchRemoteVersion())),
          tap((remote) => {
            this.ngZone.run(() => this.handleRemoteVersion(remote));
          }),
        )
        .subscribe(),
    );

    this.subscriptions.add(
      fromEvent(document, 'visibilitychange')
        .pipe(
          filter(() => document.visibilityState === 'visible'),
          // Re-check on focus so a new deploy is picked up promptly (AC1). We do NOT
          // reset `notifiedVersion` here: the per-version guard in handleRemoteVersion
          // already re-prompts for a *different* version, while a version the user has
          // already dismissed with "Later" stays dismissed (no per-focus nag).
          tap(() => this.checkForUpdate()),
        )
        .subscribe(),
    );
  }

  stop(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.started = false;
    this.promptOpen = false;

    this.channel?.close();
    this.channel = null;

    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
  }

  checkForUpdate(): void {
    if (!this.config.enabled || this.promptOpen) {
      return;
    }
    void this.fetchRemoteVersion().then((remote) => {
      this.ngZone.run(() => this.handleRemoteVersion(remote));
    });
  }

  /**
   * True when the deployed fingerprint differs from the one running in this tab.
   * Note: this detects *any change*, so a rollback to an older build also counts.
   */
  hasVersionChanged(current: AppVersionInfo, remote: AppVersionInfo): boolean {
    if (!current?.version || !remote?.version) {
      return false;
    }
    return current.version !== remote.version || current.buildTime !== remote.buildTime;
  }

  private async fetchRemoteVersion(): Promise<AppVersionInfo | null> {
    try {
      const url = this.buildVersionUrl();
      const response = await fetch(url, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
      if (!response.ok) {
        this.log(`HTTP ${response.status} for ${url}`);
        return null;
      }
      const body: unknown = await response.json();
      return this.normalizeVersion(body);
    } catch (err) {
      this.log('Fetch failed', err);
      return null;
    }
  }

  /** Resolve `version.json` against `<base href>` so it works under a sub-path deploy. */
  private buildVersionUrl(): string {
    const url = new URL('version.json', document.baseURI);
    url.searchParams.set('t', String(Date.now()));
    return url.toString();
  }

  private normalizeVersion(body: unknown): AppVersionInfo | null {
    if (!body || typeof body !== 'object') {
      return null;
    }
    const version = (body as { version?: unknown }).version;
    const buildTime = (body as { buildTime?: unknown }).buildTime;
    if (typeof version !== 'string' || !version.trim()) {
      return null;
    }
    return {
      version: version.trim(),
      buildTime: typeof buildTime === 'string' ? buildTime : '',
    };
  }

  private handleRemoteVersion(remote: AppVersionInfo | null): void {
    if (!remote) {
      return;
    }

    const changed = this.hasVersionChanged(this.runningVersion, remote);
    this.log('Compare', { running: this.runningVersion, remote, changed });

    if (!changed || this.notifiedVersion === remote.version || this.promptOpen) {
      return;
    }

    // Let sibling tabs know so they prompt without waiting for their next poll (AC7).
    this.channel?.postMessage(remote);
    this.promptUserToRefresh(remote);
  }

  private promptUserToRefresh(remote: AppVersionInfo): void {
    this.promptOpen = true;
    this.notifiedVersion = remote.version;

    const hasUnsaved = this.unsavedChanges.hasUnsavedChanges();
    this.log('Showing update dialog', { remote, hasUnsaved });

    const message = this.t(
      'newVersionAvailable',
      'Reload to continue using the latest version and improvements.',
    );
    const unsavedWarning = this.t(
      'newVersionUnsavedWarning',
      'Unsaved changes will be lost if you reload before saving.',
    );
    const html = hasUnsaved
      ? `<p>${this.escapeHtml(message)}</p><p style="margin-top:0.75rem;font-weight:600;">${this.escapeHtml(unsavedWarning)}</p>`
      : `<p>${this.escapeHtml(message)}</p>`;

    void Swal.fire({
      icon: hasUnsaved ? 'warning' : 'info',
      title: this.t(
        'newVersionAvailableTitle',
        'A new version of the application is available.',
      ),
      html,
      showCancelButton: true,
      confirmButtonText: this.t('reloadNow', 'Reload Now'),
      cancelButtonText: this.t('later', 'Later'),
      // Theme greys — overrides SweetAlert2 default purple (#7066e0).
      confirmButtonColor: '#636363',
      cancelButtonColor: '#545454',
      allowOutsideClick: false,
      allowEscapeKey: true,
      reverseButtons: true,
      heightAuto: false,
    }).then((result) => {
      this.promptOpen = false;
      if (result.isConfirmed) {
        this.reloadApplication();
      }
    });
  }

  /** Hard navigation so the browser re-requests index.html instead of bfcache/memory cache. */
  reloadApplication(): void {
    // We already warned about unsaved data in the modal — suppress the native
    // beforeunload prompt so the user isn't asked twice.
    this.reloading = true;
    const url = new URL(window.location.href);
    url.searchParams.set(RELOAD_PARAM, String(Date.now()));
    window.location.replace(url.toString());
  }

  private registerCrossTabSync(): void {
    if (this.channel || typeof BroadcastChannel === 'undefined') {
      return;
    }
    this.channel = new BroadcastChannel(VERSION_CHANNEL);
    this.channel.onmessage = (event: MessageEvent) => {
      const remote = this.normalizeVersion(event.data);
      if (remote) {
        this.ngZone.run(() => this.handleRemoteVersion(remote));
      }
    };
  }

  private registerUnloadGuard(): void {
    if (this.beforeUnloadHandler) {
      return;
    }
    this.beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      if (this.reloading || !this.unsavedChanges.hasUnsavedChanges()) {
        return;
      }
      event.preventDefault();
      // Legacy Chrome requires a truthy returnValue to show the native prompt.
      Reflect.set(event, 'returnValue', '');
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  /** Drop the `_v` cache-busting param the reload added, without a further navigation. */
  private cleanupReloadParam(): void {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(RELOAD_PARAM)) {
      return;
    }
    url.searchParams.delete(RELOAD_PARAM);
    const query = url.searchParams.toString();
    const cleaned = `${url.pathname}${query ? `?${query}` : ''}${url.hash}`;
    window.history.replaceState(window.history.state, '', cleaned);
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private t(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return !value || value === key ? fallback : value;
  }

  private log(message: string, data?: unknown): void {
    if (environment.production) {
      return;
    }
    if (data !== undefined) {
      console.log(`[VersionCheck] ${message}`, data);
    } else {
      console.log(`[VersionCheck] ${message}`);
    }
  }
}
